import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ActiveFilters, previewGatedProjectStatuses, SavedView } from "@app/network-portfolio-management/interfaces";
import { emptyActiveFilters, getDefaultView, setDefaultView as persistDefaultView } from "@app/network-portfolio-management/filterStorage";
import { normalizedFiltersJson, savedViewsQueryKey, toSavedView } from "@app/network-portfolio-management/utils/savedViewsUtils";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";

export const ALL_PROJECTS_VIEW_ID = -1;

const ALL_PROJECTS_VIEW: SavedView = {
  description: null,
  failedFilterProperties: [],
  filters: emptyActiveFilters(),
  hasFilterLoadError: false,
  isDefault: false,
  isQesView: true,
  name: "All Projects",
  npmFilteredViewId: ALL_PROJECTS_VIEW_ID,
};

type ActiveFiltersArg = ActiveFilters | ((prev: ActiveFilters) => ActiveFilters);

export interface FilterContextProps {
  activeFilters: ActiveFilters;
  activeSavedViewId: number | null;
  addView: (view: SavedView) => void;
  applyView: (id: number) => void;
  /** Whether the current user holds the Preview role, so the filter bar can offer the Preview-gated statuses. */
  canUsePreviewStatuses: boolean;
  clearActiveView: () => void;
  defaultViewId: number | null;
  deleteView: (id: number) => Promise<void>;
  error: string | null;
  loading: boolean;
  replaceView: (oldId: number, newView: SavedView, activate?: boolean) => void;
  /**
   * True when the currently-applied saved view carried a permission-restricted filter that was dropped because the
   * user lacks the required role — a Project Action or Project Utilization filter, or a Preview-gated project status
   * (Publish Pending / Not Run). Drives the permission-loss banner above the filter bar.
   */
  restrictedFiltersDropped: boolean;
  savedViewFailedFilterProperties: readonly string[];
  savedViewFilterLoadError: boolean;
  savedViews: SavedView[];
  setActiveFilters: (filtersOrUpdater: ActiveFiltersArg) => void;
  setDefaultView: (id: number | null) => Promise<void>;
}

export const FilterContext = createContext<FilterContextProps>({
  activeFilters: emptyActiveFilters(),
  activeSavedViewId: null,
  addView: () => {},
  applyView: () => {},
  canUsePreviewStatuses: false,
  clearActiveView: () => {},
  defaultViewId: null,
  deleteView: () => Promise.resolve(),
  error: null,
  loading: false,
  replaceView: () => {},
  restrictedFiltersDropped: false,
  savedViewFailedFilterProperties: [],
  savedViewFilterLoadError: false,
  savedViews: [],
  setActiveFilters: () => {},
  setDefaultView: () => Promise.resolve(),
});

export const useFilterContext = (): FilterContextProps => useContext(FilterContext);

interface FilterContextProviderProps {
  children: ReactNode;
}

export const FilterContextProvider: FC<FilterContextProviderProps> = ({ children }) => {
  const npmService = useRef(new NetworkPortfolioManagementService()).current;
  const user = useContext(UserContext);
  const userId = user?.userId;
  const queryClient = useQueryClient();
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);

  const savedViewsEnabled = enabledFeatureFlags.has(Features.PROJECT_ARGUS_PROJECT_DASHBOARD_SAVED_VIEWS);

  // There are filters restricted to the internal roles. They persist inside ActiveFilters (saved views, default view), so a value set by a
  // permitted user — including via a shared Quest view — can reach a user without those roles. Coerce them away for
  // unauthorized users at this single source so every consumer (chip, Key Metrics, selection bar, the server fetch)
  // stays consistent with the server, which already ignores the filters for them.
  const canUseRestrictedFilters = useMemo(
    () => !!user?.roles?.some((role) => role === Roles.QUEST_USER || role === Roles.CLIENT_ADMIN),
    [user],
  );

  // Publish Pending and Not Run project statuses are gated behind the Preview role. Like projectAction, the
  // selection persists inside ActiveFilters, so a shared/saved view built by a Preview user can reach a
  // non-Preview user. Strip the gated values at this single source (mirrors the backend, which ignores them).
  const canUsePreviewStatuses = useMemo(
    () => !!user?.roles?.some((role) => role === Roles.PREVIEW),
    [user],
  );

  const [activeFilters, setActiveFiltersState] = useState<ActiveFilters>(
    () => (savedViewsEnabled ? getDefaultView(userId).filters : emptyActiveFilters()),
  );
  const [activeSavedViewId, setActiveSavedViewIdState] = useState<number | null>(
    () => (savedViewsEnabled ? getDefaultView(userId).npmFilteredViewId : null),
  );

  // Set when an applied saved view's role-restricted filter was dropped for a
  // user without the role; drives the banner.
  const [restrictedFiltersDropped, setRestrictedFiltersDropped] = useState(false);
  // Set when an applied saved view had a filter the backend could not deserialize (its saved shape is stale);
  // drives the "re-save this view" warning. Holds the first failed filter's name (may be empty when unknown).
  const [savedViewLoadError, setSavedViewLoadError] = useState<{ failedProperties: readonly string[] } | null>(null);
  // False until the initial view has been synced from the server's copy (see the reconcile effect below).
  const [reconciledWithServer, setReconciledWithServer] = useState(false);
  // activeSavedViewId as of the latest render, for reads after an await (see deleteView).
  const activeSavedViewIdRef = useRef(activeSavedViewId);
  useEffect(() => { activeSavedViewIdRef.current = activeSavedViewId; }, [activeSavedViewId]);
  const setActiveFilters = useCallback((filtersOrUpdater: ActiveFiltersArg): void => {
    setActiveFiltersState((prev) =>
      typeof filtersOrUpdater === "function" ? filtersOrUpdater(prev) : filtersOrUpdater,
    );
  }, []);

  // These filters live inside ActiveFilters so they persist with saved views and are read/written like any other
  // filter (via activeFilters / setActiveFilters). The activeFilters exposed below coerces the restricted values away
  // for users without the required role, so every consumer stays consistent with the server, which already ignores
  // those filters for them.
  const effectiveActiveFilters = useMemo<ActiveFilters>(() => {
    let effective = activeFilters;
    if (!canUseRestrictedFilters
      && (effective.projectAction !== null || effective.projectUtilizationCategoryIds.length > 0)) {
      effective = { ...effective, projectAction: null, projectUtilizationCategoryIds: [] };
    }
    if (!canUsePreviewStatuses && effective.projectStatuses.some((s) => previewGatedProjectStatuses.includes(s))) {
      effective = { ...effective, projectStatuses: effective.projectStatuses.filter((s) => !previewGatedProjectStatuses.includes(s)) };
    }
    return effective;
  }, [canUseRestrictedFilters, canUsePreviewStatuses, activeFilters]);

  const { data: fetchedViews = [], isLoading: loading, error: queryError } = useQuery({
    enabled: savedViewsEnabled,
    queryFn: ({ signal }) => npmService.getSavedViews(signal),
    queryKey: savedViewsQueryKey(userId),
    select: (results) => results.map(toSavedView),
    staleTime: 5 * 60 * 1000,
  });

  const savedViews = useMemo(() => [ALL_PROJECTS_VIEW, ...fetchedViews], [fetchedViews]);

  const error = queryError ? "Failed to retrieve saved views. Please try again" : null;

  // ID of the explicitly designated default view. null means All Projects is the implied default.
  const defaultViewId = useMemo(
    () => fetchedViews.find((v) => v.isDefault)?.npmFilteredViewId ?? null,
    [fetchedViews],
  );

  // Sync the current default view to localStorage once loading completes and whenever it changes.
  // This keeps the stored snapshot fresh so the correct view is applied on next page load.
  useEffect(() => {
    if (!savedViewsEnabled || loading || queryError) return;
    const view = defaultViewId !== null ? fetchedViews.find((v) => v.npmFilteredViewId === defaultViewId) : null;
    persistDefaultView(userId, defaultViewId, view?.filters ?? emptyActiveFilters());
  }, [defaultViewId, fetchedViews, loading, queryError, savedViewsEnabled, userId]);

  // Clear the active view when filters no longer match the saved view's snapshot,
  // or when the stored default view no longer exists in the loaded list.
  useEffect(() => {
    if (!reconciledWithServer) return;
    if (activeSavedViewId === null) return;
    const activeView = savedViews.find((v) => v.npmFilteredViewId === activeSavedViewId);
    if (!activeView) {
      if (!loading) {
        setActiveSavedViewIdState(null);
        setActiveFilters(emptyActiveFilters());
        setRestrictedFiltersDropped(false);
        setSavedViewLoadError(null);
      }
      return;
    }
    if (normalizedFiltersJson(activeFilters) !== normalizedFiltersJson(activeView.filters)) {
      setActiveSavedViewIdState(null);
      setRestrictedFiltersDropped(false);
      setSavedViewLoadError(null);
    }
  }, [activeFilters, activeSavedViewId, loading, reconciledWithServer, savedViews, setActiveFilters]);

  const applyView = useCallback((id: number): void => {
    if (!savedViewsEnabled) return;
    const view = savedViews.find((v) => v.npmFilteredViewId === id);
    if (!view) return;
    setActiveFilters(view.filters);
    setActiveSavedViewIdState(id);
    // Restricted values are coerced away for unauthorized users (effectiveActiveFilters); flag it so the banner
    // can explain that saved filters weren't applied. Re-evaluated on every apply, so a clean view clears the flag.
    const droppedRestricted = !canUseRestrictedFilters
      && (view.filters.projectAction !== null || (view.filters.projectUtilizationCategoryIds?.length ?? 0) > 0);
    const droppedStatuses = !canUsePreviewStatuses
      && view.filters.projectStatuses.some((s) => previewGatedProjectStatuses.includes(s));
    setRestrictedFiltersDropped(droppedRestricted || droppedStatuses);
    // Surface a warning when the persisted filters could not be fully loaded (a filter's saved shape is stale), so
    // the user knows the view is incomplete and must be re-saved. Re-evaluated on every apply, so a clean view clears it.
    setSavedViewLoadError(view.hasFilterLoadError ? { failedProperties: view.failedFilterProperties ?? [] } : null);
  }, [canUseRestrictedFilters, canUsePreviewStatuses, savedViews, savedViewsEnabled, setActiveFilters]);

  useEffect(() => {
    if (reconciledWithServer || loading) return;
    // A failed fetch means we don't know the server's default - leave reconciliation pending (and the
    // localStorage-seeded state untouched) rather than treating the error as "no default view."
    if (queryError) return;
    // Runs once per load (guarded by reconciledWithServer, not repeated on background refetches). The API
    // response is authoritative and always wins over the localStorage seed - a default view is only ever set
    // by this same user, so the seeded id and the server's current default agree except when the view was
    // since deleted, which the fallback below handles.
    if (defaultViewId !== null) {
      applyView(defaultViewId);
    } else if (activeSavedViewId !== null) {
      setActiveSavedViewIdState(null);
      setActiveFilters(emptyActiveFilters());
      setRestrictedFiltersDropped(false);
      setSavedViewLoadError(null);
    }
    setReconciledWithServer(true);
  }, [activeSavedViewId, applyView, defaultViewId, loading, queryError, reconciledWithServer, setActiveFilters]);

  const clearActiveView = useCallback((): void => {
    setActiveSavedViewIdState(null);
    setRestrictedFiltersDropped(false);
    setSavedViewLoadError(null);
  }, []);

  const addView = useCallback((view: SavedView): void => {
    if (!savedViewsEnabled) return;
    queryClient.setQueryData<SavedView[]>(savedViewsQueryKey(userId), (prev) => {
      // When the new view is the default, clear isDefault from all others in the cache.
      const base = (prev ?? []).map((v) => view.isDefault && v.isDefault ? { ...v, isDefault: false } : v);
      return [...base, view];
    });
    setActiveSavedViewIdState(view.npmFilteredViewId);
    // The newly saved view becomes the active one, so its load-error state replaces the previous view's. Without
    // this the stale-view banner would outlive the re-save it tells the user to perform: the saved view's filters
    // match activeFilters, so the staleness effect never fires to clear it either.
    setSavedViewLoadError(view.hasFilterLoadError ? { failedProperties: view.failedFilterProperties ?? [] } : null);
  }, [queryClient, savedViewsEnabled, userId]);

  // Swaps in place to preserve row order. Only retargets activeSavedViewId when the
  // replaced view was already active OR activate=true is passed (overwrite path in
  // SavedViewsButton), so editing from ManageViewsModal doesn't silently switch the
  // user's applied view.
  const replaceView = useCallback((oldId: number, newView: SavedView, activate = false): void => {
    if (!savedViewsEnabled) return;
    const current = queryClient.getQueryData<SavedView[]>(savedViewsQueryKey(userId)) ?? [];
    const idx = current.findIndex((v) => v.npmFilteredViewId === oldId);
    if (idx === -1) return;
    // When the replacement is now the default, clear isDefault from other views in cache.
    const updated = current.map((v) => newView.isDefault && v.isDefault && v.npmFilteredViewId !== oldId ? { ...v, isDefault: false } : v);
    queryClient.setQueryData<SavedView[]>(savedViewsQueryKey(userId),
      [...updated.slice(0, idx), newView, ...updated.slice(idx + 1)]);
    const retargetsActiveView = activeSavedViewId === oldId || activate;
    setActiveSavedViewIdState((prev) => {
      if (prev !== oldId && !activate) return prev;
      return newView.npmFilteredViewId;
    });
    // Only when this replaces the view currently applied: the load-error state belongs to the active view, so
    // editing some other view from ManageViewsModal must not clear a banner that is still accurate. This is what
    // makes "Re-save the view to ... clear this message" actually true on the overwrite path.
    if (retargetsActiveView) {
      setSavedViewLoadError(newView.hasFilterLoadError ? { failedProperties: newView.failedFilterProperties ?? [] } : null);
    }
  }, [activeSavedViewId, queryClient, savedViewsEnabled, userId]);

  const deleteView = useCallback(async (id: number): Promise<void> => {
    if (!savedViewsEnabled) return;
    await npmService.deleteSavedView(id);
    queryClient.setQueryData<SavedView[]>(savedViewsQueryKey(userId),
      (prev) => (prev ?? []).filter((v) => v.npmFilteredViewId !== id));
    // The view active now, not when the delete started - the user can switch mid-flight.
    const deletedActiveView = activeSavedViewIdRef.current === id;
    setActiveSavedViewIdState((prev) => {
      if (prev !== id) return prev;
      return null;
    });
    // Both banners describe the active view, and the staleness effect can't clear them once the id is null.
    if (deletedActiveView) {
      setRestrictedFiltersDropped(false);
      setSavedViewLoadError(null);
    }
  }, [npmService, queryClient, savedViewsEnabled, userId]);

  const setDefaultView = useCallback(async (id: number | null): Promise<void> => {
    if (!savedViewsEnabled) return;
    if (id === null) {
      // Clear the default from whichever custom view currently holds it.
      const currentDefault = fetchedViews.find((v) => v.isDefault);
      if (!currentDefault) return;
      const response = await npmService.updateSavedView(currentDefault.npmFilteredViewId, {
        description: currentDefault.description ?? "",
        filters: currentDefault.filters,
        isDefault: false,
        name: currentDefault.name,
      });
      queryClient.setQueryData<SavedView[]>(savedViewsQueryKey(userId),
        (prev) => (prev ?? []).map((v) => v.npmFilteredViewId === response.npmFilteredViewId ? toSavedView(response) : v));
    } else {
      const view = fetchedViews.find((v) => v.npmFilteredViewId === id);
      if (!view) return;
      const response = await npmService.updateSavedView(id, {
        description: view.description ?? "",
        filters: view.filters,
        isDefault: true,
        name: view.name,
      });
      queryClient.setQueryData<SavedView[]>(savedViewsQueryKey(userId), (prev) =>
        (prev ?? []).map((v) => {
          if (v.npmFilteredViewId === id) return toSavedView(response);
          if (v.isDefault) return { ...v, isDefault: false };
          return v;
        }),
      );
    }
  }, [fetchedViews, npmService, queryClient, savedViewsEnabled, userId]);

  return (
    <FilterContext.Provider
      value={{
        activeFilters: effectiveActiveFilters,
        activeSavedViewId,
        addView,
        applyView,
        canUsePreviewStatuses,
        clearActiveView,
        defaultViewId,
        deleteView,
        error,
        loading,
        replaceView,
        restrictedFiltersDropped,
        savedViewFailedFilterProperties: savedViewLoadError?.failedProperties ?? [],
        savedViewFilterLoadError: savedViewLoadError !== null,
        savedViews,
        setActiveFilters,
        setDefaultView,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};
